import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Edit, Download, Printer, Share2, 
  ThumbsUp, ThumbsDown, Eye, User, Calendar, 
  Tag as TagIcon, FileText, Link as LinkIcon,
  Package, Layers, AlertCircle, Clock, TrendingUp,
  MessageSquare, BookOpen, CheckCircle, ExternalLink,
  Paperclip, HelpCircle, Mail, File
} from 'lucide-react';
import { Button, Card, Typography, message, Spin, Space, Divider, Row, Col, Badge, Empty, Tag, Tooltip, List, Modal, Dropdown, Menu } from 'antd';
import api from '../../utils/api';
import './KnowledgeBase.css';

const { Title, Text, Paragraph } = Typography;

const ArticleViewPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [helpfulLoading, setHelpfulLoading] = useState(false);
  const [notHelpfulLoading, setNotHelpfulLoading] = useState(false);
  const [relatedArticles, setRelatedArticles] = useState([]);
  const [showShareOptions, setShowShareOptions] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [isSupportModalVisible, setIsSupportModalVisible] = useState(false);
  const [supportTicket, setSupportTicket] = useState(null);
  const [supportLoading, setSupportLoading] = useState(false);
  
  // Use refs to track state
  const isFetching = useRef(false);
  const mounted = useRef(true);

  // SINGLE useEffect to handle all initialization
  useEffect(() => {
    console.log('🔍 ArticleViewPage mounted for article:', id);
    
    // Mark as mounted
    mounted.current = true;
    
    // Fetch article data
    fetchArticle();
    
    // Cleanup function
    return () => {
      console.log('🔍 ArticleViewPage unmounting');
      mounted.current = false;
    };
  }, [id]);

  // Separate useEffect for related articles (non-blocking)
  useEffect(() => {
    // Only fetch related articles after main article is loaded
    if (!loading && article && relatedArticles.length === 0) {
      setTimeout(() => {
        fetchRelatedArticles();
      }, 300);
    }
  }, [loading, article]);

  const fetchArticle = async () => {
    // Prevent multiple concurrent fetches
    if (isFetching.current) {
      console.log('⚠️ Already fetching article, skipping duplicate call');
      return;
    }

    try {
      isFetching.current = true;
      setLoading(true);
      
      console.log('📄 Fetching article:', id);
      const response = await api.get(`/api/kb-articles/${id}`);
      
      if (!mounted.current) {
        console.log('⚠️ Component unmounted, ignoring response');
        return;
      }
      
      if (response.data.success) {
        const articleData = response.data.data;
        setArticle(articleData);
        
        // Fetch attachments for this article
        fetchArticleAttachments(articleData.id || articleData.articleUuid);
        
        console.log('✅ Article loaded');
      } else {
        message.error(response.data.message || 'Article not found');
      }
    } catch (error) {
      console.error('Error fetching article:', error);
      if (mounted.current) {
        message.error('Failed to load article');
      }
    } finally {
      if (mounted.current) {
        setLoading(false);
      }
      isFetching.current = false;
    }
  };

  // Fetch attachments for the article
  const fetchArticleAttachments = async (articleId) => {
    try {
      console.log('📎 Fetching attachments for article:', articleId);
      
      // Try different API endpoints based on your backend structure
      const endpoints = [
        `/api/kb-articles/${articleId}/attachments`,
        `/api/attachments/article/${articleId}`,
        `/api/kb-article-attachments?article_id=${articleId}`
      ];
      
      let attachmentsData = [];
      
      for (const endpoint of endpoints) {
        try {
          const response = await api.get(endpoint);
          if (response.data.success && response.data.data) {
            attachmentsData = Array.isArray(response.data.data) ? response.data.data : [response.data.data];
            console.log(`✅ Attachments found via ${endpoint}:`, attachmentsData.length);
            break;
          }
        } catch (err) {
          console.log(`⚠️ Endpoint ${endpoint} not available`);
        }
      }
      
      // If no attachments found via API, check if article has attachments property
      if (attachmentsData.length === 0 && article?.attachments) {
        attachmentsData = Array.isArray(article.attachments) ? article.attachments : [];
      }
      
      setAttachments(attachmentsData);
      
      if (attachmentsData.length === 0) {
        console.log('📎 No attachments found for this article');
      }
      
    } catch (error) {
      console.error('Error fetching attachments:', error);
      // Don't show error to user, just log it
    }
  };

  const fetchRelatedArticles = async () => {
    try {
      console.log('📚 Fetching related articles');
      const response = await api.get('/api/kb-articles', {
        params: {
          limit: 4,
          sortBy: 'popular',
          status: 'published'
        }
      });
      
      if (response.data.success && mounted.current) {
        const filtered = response.data.data
          .filter(item => item.id != id && item.articleUuid != id)
          .slice(0, 3);
        console.log('✅ Related articles found:', filtered.length);
        setRelatedArticles(filtered);
      }
    } catch (error) {
      console.error('Error fetching related articles:', error);
    }
  };

  // Fetch support ticket details
  const fetchSupportTicket = async () => {
    if (!article?.ticketId && !article?.ticketUuid) {
      message.warning('No linked support ticket found');
      return;
    }

    setSupportLoading(true);
    try {
      // Use the ticket ID from the article
      const ticketId = article.ticketId || article.ticketUuid;
      console.log('🎫 Fetching support ticket from API:', ticketId);
      
      // Use the correct API endpoint
      const response = await api.get(`/api/tickets/${ticketId}`);
      console.log('📊 API Response:', response.data);
      
      if (response.data.success && response.data.data) {
        const ticketData = response.data.data;
        
        // Map the API response to our expected format
        const mappedTicket = mapTicketData(ticketData);
        setSupportTicket(mappedTicket);
        setIsSupportModalVisible(true);
      } else {
        message.warning('Ticket details not available. The ticket may have been deleted or you may not have access.');
      }
    } catch (error) {
      console.error('Error fetching support ticket:', error);
      
      // Check if it's a 404 error (ticket not found)
      if (error.response && error.response.status === 404) {
        message.warning('Ticket not found. It may have been deleted.');
      } else {
        message.error('Failed to load ticket details. Please try again.');
      }
    } finally {
      setSupportLoading(false);
    }
  };

  // Helper function to map ticket data from API response
  const mapTicketData = (ticket) => {
    console.log('🔄 Mapping ticket data:', ticket);
    
    // Debug: Log comments structure
    console.log('💬 Raw comments from API:', ticket.comments);
    
    return {
      // Ticket ID
      id: ticket.id,
      ticketId: ticket.id,
      ticketUuid: ticket.ticketUuid,
      ticketNumber: ticket.ticketUuid,
      
      // Basic info
      subject: ticket.title,
      description: ticket.description,
      
      // Status - use status_name from API
      status: ticket.statusName ? ticket.statusName.toLowerCase() : 'unknown',
      statusName: ticket.statusName,
      
      // Priority - use priority_name from API
      priority: ticket.priorityName ? ticket.priorityName.toLowerCase() : 'normal',
      priorityName: ticket.priorityName,
      
      // Assigned to - combine first and last name
      assignedTo: ticket.assigneeFirstName && ticket.assigneeLastName 
        ? `${ticket.assigneeFirstName} ${ticket.assigneeLastName}`
        : ticket.assigneeFirstName || ticket.assigneeLastName || 'Unassigned',
      assigneeFirstName: ticket.assigneeFirstName,
      assigneeLastName: ticket.assigneeLastName,
      
      // Created by
      createdBy: ticket.creatorFirstName && ticket.creatorLastName
        ? `${ticket.creatorFirstName} ${ticket.creatorLastName}`
        : ticket.creatorFirstName || ticket.creatorLastName || 'Unknown',
      creatorFirstName: ticket.creatorFirstName,
      creatorLastName: ticket.creatorLastName,
      
      // Dates
      createdAt: ticket.createdAt,
      updatedAt: ticket.updatedAt,
      caseReportedDate: ticket.caseReportedDate,
      dueDate: ticket.dueDate,
      resolvedAt: ticket.resolvedAt,
      closedAt: ticket.closedAt,
      
      // Category and Type
      category: ticket.ticketCategoryName,
      type: ticket.ticketTypeName,
      productCategory: ticket.productCategoryName,
      product: ticket.productName,
      
      // Organization
      organizationName: ticket.organizationName,
      contractName: ticket.contractName,
      
      // Status tracking dates
      firstResponseAt: ticket.firstResponseAt,
      lastCustomerContactAt: ticket.lastCustomerContactAt,
      
      // Additional fields
      internalDescription: ticket.internalDescription,
      resolutionTaken: ticket.resolutionTaken,
      cause: ticket.cause,
      impact: ticket.impact,
      remedialActions: ticket.remedialActions,
      
      // Review/Approval info
      contractorReviewer: ticket.contractorReviewerFirstName && ticket.contractorReviewerLastName
        ? `${ticket.contractorReviewerFirstName} ${ticket.contractorReviewerLastName}`
        : null,
      lhdnmReviewer: ticket.lhdnmReviewerFirstName && ticket.lhdnmReviewerLastName
        ? `${ticket.lhdnmReviewerFirstName} ${ticket.lhdnmReviewerLastName}`
        : null,
      lhdnmApprover: ticket.lhdnmApproverFirstName && ticket.lhdnmApproverLastName
        ? `${ticket.lhdnmApproverFirstName} ${ticket.lhdnmApproverLastName}`
        : null,
      
      // Comments - map properly with correct field names from database
      comments: ticket.comments ? ticket.comments.map(comment => ({
        id: comment.id,
        content: comment.commentText || comment.content || comment.comment_text || '',
        commentText: comment.commentText || comment.comment_text || comment.content || '',
        createdAt: comment.createdAt || comment.created_at,
        userId: comment.userId || comment.user_id,
        isSystemEvent: comment.isSystemEvent || comment.is_system_event,
        eventType: comment.eventType || comment.event_type,
        oldValue: comment.oldValue || comment.old_value,
        newValue: comment.newValue || comment.new_value,
        
        // User information - check multiple possible field names
        userFirstName: comment.userFirstName || comment.first_name || comment.user_first_name,
        userLastName: comment.userLastName || comment.last_name || comment.user_last_name,
        userEmail: comment.userEmail || comment.email || comment.user_email,
        userName: (comment.userFirstName || comment.first_name || comment.user_first_name) && 
                  (comment.userLastName || comment.last_name || comment.user_last_name)
          ? `${comment.userFirstName || comment.first_name || comment.user_first_name} ${comment.userLastName || comment.last_name || comment.user_last_name}`
          : comment.userFirstName || comment.first_name || comment.user_first_name || 
            comment.userEmail || comment.email || comment.user_email || 'System'
      })).filter(comment => comment.content && comment.content.trim() !== '') : [],
      
      // Raw data for reference
      _raw: ticket
    };
  };

  // Helper function to get priority info
  const getPriorityInfo = (priorityId) => {
    // Handle both numeric IDs and string priorities
    const priorities = {
      // Numeric IDs
      1: { label: 'High', color: '#dc2626', bgColor: '#fef2f2', icon: '🔥' },
      2: { label: 'Medium', color: '#ea580c', bgColor: '#fff7ed', icon: '⚡' },
      3: { label: 'Low', color: '#16a34a', bgColor: '#f0fdf4', icon: '🌱' },
      4: { label: 'Critical', color: '#7c3aed', bgColor: '#f5f3ff', icon: '🚨' },
      // String priorities (normalized)
      'critical': { label: 'Critical', color: '#7c3aed', bgColor: '#f5f3ff', icon: '🚨' },
      'high': { label: 'High', color: '#dc2626', bgColor: '#fef2f2', icon: '🔥' },
      'medium': { label: 'Medium', color: '#ea580c', bgColor: '#fff7ed', icon: '⚡' },
      'low': { label: 'Low', color: '#16a34a', bgColor: '#f0fdf4', icon: '🌱' },
      'normal': { label: 'Normal', color: '#4b5563', bgColor: '#f3f4f6', icon: '📝' }
    };
    
    return priorities[priorityId] || { label: 'Normal', color: '#4b5563', bgColor: '#f3f4f6', icon: '📝' };
  };

  // Helper function to get status info
  const getStatusInfo = (status) => {
    // Handle normalized status values
    const statuses = {
      'published': { label: 'Published', color: '#059669', bgColor: '#d1fae5', icon: <CheckCircle size={12} /> },
      'draft': { label: 'Draft', color: '#d97706', bgColor: '#fef3c7', icon: <FileText size={12} /> },
      'archived': { label: 'Archived', color: '#6b7280', bgColor: '#f3f4f6', icon: <BookOpen size={12} /> },
      'review': { label: 'Under Review', color: '#2563eb', bgColor: '#dbeafe', icon: <MessageSquare size={12} /> },
      // Ticket statuses
      'open': { label: 'Open', color: '#ef4444', bgColor: '#fef2f2', icon: <AlertCircle size={12} /> },
      'pending': { label: 'Pending', color: '#f59e0b', bgColor: '#fef3c7', icon: <Clock size={12} /> },
      'in progress': { label: 'In Progress', color: '#3b82f6', bgColor: '#dbeafe', icon: <Clock size={12} /> },
      'resolved': { label: 'Resolved', color: '#10b981', bgColor: '#d1fae5', icon: <CheckCircle size={12} /> },
      'closed': { label: 'Closed', color: '#6b7280', bgColor: '#f3f4f6', icon: <CheckCircle size={12} /> }
    };
    
    return statuses[status] || { label: 'Unknown', color: '#6b7280', bgColor: '#f3f4f6', icon: <FileText size={12} /> };
  };

  // Helper function to get type info
  const getTypeInfo = (typeName) => {
    const types = {
      'Troubleshooting Guide': { color: '#dc2626', icon: '🔧' },
      'How-To Guide': { color: '#059669', icon: '📖' },
      'Standard Operating Procedure': { color: '#2563eb', icon: '📋' },
      'Administrator Guide': { color: '#7c3aed', icon: '👨‍💼' },
      'Reference Documentation': { color: '#9333ea', icon: '📚' },
      'FAQ': { color: '#ea580c', icon: '❓' },
      'Tutorial': { color: '#0d9488', icon: '🎓' },
      'Knowledge Base Article': { color: '#4f46e5', icon: '💡' }
    };
    return types[typeName] || { color: '#6b5563', icon: '📄' };
  };

  const handleHelpfulVote = async (isHelpful) => {
    if (isHelpful) {
      setHelpfulLoading(true);
      try {
        // Check if this endpoint exists
        await api.post(`/api/kb-articles/${id}/feedback`, { isHelpful: true });
        message.success('Thank you for your feedback! This article has been marked as helpful.');
        // Refresh article data to show updated counts
        fetchArticle();
      } catch (error) {
        console.error('Error submitting helpful vote:', error);
        message.error('Failed to submit feedback');
      } finally {
        setHelpfulLoading(false);
      }
    } else {
      setNotHelpfulLoading(true);
      try {
        await api.post(`/api/kb-articles/${id}/feedback`, { isHelpful: false });
        message.info('Thanks for your feedback. We\'ll use it to improve this article.');
        // Refresh article data to show updated counts
        fetchArticle();
      } catch (error) {
        console.error('Error submitting not helpful vote:', error);
        message.error('Failed to submit feedback');
      } finally {
        setNotHelpfulLoading(false);
      }
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'N/A';
      
      const now = new Date();
      const diffMs = now - date;
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
      
      if (diffDays === 0) {
        return 'Today';
      } else if (diffDays === 1) {
        return 'Yesterday';
      } else if (diffDays < 7) {
        return `${diffDays} days ago`;
      } else if (diffDays < 30) {
        const weeks = Math.floor(diffDays / 7);
        return `${weeks} week${weeks > 1 ? 's' : ''} ago`;
      }
      
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (error) {
      console.error('Date formatting error:', error);
      return 'N/A';
    }
  };

  const parseMarkdownContent = (content) => {
    if (!content) return '<p>No content available</p>';
    
    // Enhanced Markdown parsing
    let html = content
      // Headers
      .replace(/^# (.*$)/gim, '<h1 class="markdown-h1">$1</h1>')
      .replace(/^## (.*$)/gim, '<h2 class="markdown-h2">$1</h2>')
      .replace(/^### (.*$)/gim, '<h3 class="markdown-h3">$1</h3>')
      .replace(/^#### (.*$)/gim, '<h4 class="markdown-h4">$1</h4>')
      .replace(/^##### (.*$)/gim, '<h5 class="markdown-h5">$1</h5>')
      .replace(/^###### (.*$)/gim, '<h6 class="markdown-h6">$1</h6>')
      // Bold and Italic
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.*?)\*/g, '<em>$1</em>')
      // Code blocks
      .replace(/`([^`]+)`/g, '<code class="inline-code">$1</code>')
      .replace(/```([\s\S]*?)```/g, '<pre><code class="block-code">$1</code></pre>')
      // Links
      .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer" class="article-link">$1</a>')
      // Blockquotes
      .replace(/^>\s*(.*$)/gim, '<blockquote>$1</blockquote>')
      // Lists
      .replace(/^\*\s+(.*$)/gim, '<li>$1</li>')
      .replace(/^\-\s+(.*$)/gim, '<li>$1</li>')
      .replace(/^\d+\.\s+(.*$)/gim, '<li>$1</li>')
      // Images
      .replace(/!\[([^\]]*)\]\(([^)]+)\)/g, '<img src="$2" alt="$1" class="article-image" />')
      // Horizontal rules
      .replace(/^---$/gim, '<hr class="article-hr" />')
      .replace(/^\*\*\*$/gim, '<hr class="article-hr" />')
      .replace(/^___$/gim, '<hr class="article-hr" />')
      // Line breaks and paragraphs
      .replace(/\n\n+/g, '</p><p>')
      .replace(/\n/g, '<br/>');
    
    // Wrap list items
    html = html.replace(/(<li>.*?<\/li>)/g, '<ul>$1</ul>');
    
    // Wrap in paragraph if needed
    if (!html.startsWith('<')) {
      html = `<p>${html}</p>`;
    }
    
    return html;
  };

  // Helper function to get article summary for sharing
  const getArticleSummary = () => {
    const articleUrl = window.location.href;
    const articleTitle = article?.title || 'Knowledge Base Article';
    const articleUuid = article?.articleUuid || 'N/A';
    const articleDescription = article?.excerpt || 'Check out this knowledge base article';
    
    // Create content preview (first 300 characters of content without HTML)
    const cleanContent = article?.content 
      ? article.content.replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim().substring(0, 300)
      : 'No content preview available.';
    
    // Create attachment links text
    let attachmentText = '';
    if (attachments.length > 0) {
      attachmentText = '\n\n📎 ATTACHMENTS:\n';
      attachments.forEach((att, index) => {
        const fileName = att.original_name || att.filename || att.name || `Attachment_${index + 1}`;
        const fileSize = att.file_size ? ` (${formatFileSize(att.file_size)})` : '';
        attachmentText += `${index + 1}. ${fileName}${fileSize}\n`;
      });
    }
    
    return `KNOWLEDGE BASE ARTICLE
================================

📋 ARTICLE ID: ${articleUuid}
📖 TITLE: ${articleTitle}

📝 DESCRIPTION: ${articleDescription}

📄 CONTENT PREVIEW:
${cleanContent}${cleanContent.length === 300 ? '...' : ''}

🔗 DIRECT LINK: ${articleUrl}

📊 ARTICLE INFORMATION:
- Category: ${article?.categoryName || 'N/A'}
- Type: ${article?.typeName || 'N/A'}
- Author: ${article?.authorName || 'N/A'}
- Published: ${formatDate(article?.publishedAt)}
- Last Updated: ${formatDate(article?.updatedAt)}${attachmentText}

---
Shared from Knowledge Base System.
For complete article with all content and attachments, visit the link above.`;
  };

  // Detect user's default email client
  const detectEmailClient = () => {
    const userAgent = navigator.userAgent.toLowerCase();
    
    // Check for Outlook desktop indicators
    if (navigator.userAgent.includes('Outlook') || 
        navigator.userAgent.includes('MSOffice') ||
        (navigator.userAgent.includes('Windows') && navigator.userAgent.includes('Mail'))) {
      return 'outlook_desktop';
    }
    
    // Check for Mail app on Mac
    if (navigator.userAgent.includes('Macintosh') && navigator.userAgent.includes('Mail')) {
      return 'apple_mail';
    }
    
    // Default to browser detection
    if (userAgent.includes('chrome')) {
      return 'chrome';
    } else if (userAgent.includes('firefox')) {
      return 'firefox';
    } else if (userAgent.includes('safari') && !userAgent.includes('chrome')) {
      return 'safari';
    } else if (userAgent.includes('edge')) {
      return 'edge';
    }
    
    return 'unknown';
  };

  // Enhanced share functionality with improved email sharing
  const handleShare = async (platform, emailClient = null) => {
    const articleSummary = getArticleSummary();
    const articleUrl = window.location.href;
    const articleTitle = article?.title || 'Knowledge Base Article';
    const articleUuid = article?.articleUuid || '';
    
    // Full title with UUID for sharing
    const fullTitle = articleUuid ? `[${articleUuid}] ${articleTitle}` : articleTitle;
    
    if (platform === 'copy') {
      const copyText = `${fullTitle}\n\n${articleSummary}\n\nLink: ${articleUrl}`;
      try {
        await navigator.clipboard.writeText(copyText);
        message.success('Article information copied to clipboard!');
      } catch (err) {
        console.error('Failed to copy:', err);
        // Fallback for older browsers
        const textArea = document.createElement('textarea');
        textArea.value = copyText;
        document.body.appendChild(textArea);
        textArea.select();
        try {
          document.execCommand('copy');
          message.success('Article information copied to clipboard!');
        } catch (e) {
          message.error('Failed to copy to clipboard');
        }
        document.body.removeChild(textArea);
      }
      setShowShareOptions(false);
      return;
    }
    
    if (platform === 'email' && emailClient) {
      if (emailClient === 'yahoo') {
        handleYahooShare(articleSummary, fullTitle);
        return;
      }
      
      if (emailClient === 'gmail') {
        handleGmailShare(articleSummary, fullTitle);
        return;
      }
      
      if (emailClient === 'outlook_desktop') {
        handleOutlookDesktopShare(articleSummary, fullTitle);
        return;
      }
      
      if (emailClient === 'outlook_web') {
        handleOutlookWebShare(articleSummary, fullTitle);
        return;
      }
      
      // Default email client - try to detect
      if (emailClient === 'default') {
        const detectedClient = detectEmailClient();
        
        if (detectedClient === 'outlook_desktop') {
          handleOutlookDesktopShare(articleSummary, fullTitle);
        } else if (detectedClient === 'apple_mail') {
          handleAppleMailShare(articleSummary, fullTitle);
        } else {
          // Generic mailto: for other clients
          handleDefaultMailShare(articleSummary, fullTitle);
        }
        return;
      }
      
    } else if (platform === 'whatsapp') {
      const whatsappText = `${fullTitle}\n\n${article?.excerpt || 'Check out this article'}\n\n${articleUrl}`;
      const url = `https://wa.me/?text=${encodeURIComponent(whatsappText)}`;
      window.open(url, '_blank', 'noopener,noreferrer');
      message.success('Opening WhatsApp...');
    }
    
    setShowShareOptions(false);
  };

  // Handle Outlook Desktop sharing
  const handleOutlookDesktopShare = (articleSummary, fullTitle) => {
    const subject = `Knowledge Base: ${fullTitle.substring(0, 60)}${fullTitle.length > 60 ? '...' : ''}`;
    const body = articleSummary;
    
    // Create mailto link with attachment references
    let mailtoUrl = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    
    // Note: mailto: doesn't support attachments directly, but we can mention them
    if (attachments.length > 0) {
      // For Outlook desktop, we can try the outlook: protocol
      try {
        // First try outlook:compose protocol
        const outlookUrl = `outlook:compose?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
        
        // Create a hidden iframe to trigger Outlook
        const iframe = document.createElement('iframe');
        iframe.style.display = 'none';
        iframe.src = outlookUrl;
        document.body.appendChild(iframe);
        
        message.success('Opening Outlook desktop app...');
        
        // Remove iframe after a short delay
        setTimeout(() => {
          document.body.removeChild(iframe);
        }, 1000);
        
        // Show attachment instructions
        if (attachments.length > 0) {
          setTimeout(() => {
            Modal.info({
              title: 'Attachments Note',
              content: (
                <div>
                  <p>Please manually attach the following files:</p>
                  <ul style={{ marginLeft: '20px', marginTop: '10px' }}>
                    {attachments.map((att, index) => (
                      <li key={index}>
                        {att.original_name || att.filename || att.name}
                        {att.file_size ? ` (${formatFileSize(att.file_size)})` : ''}
                      </li>
                    ))}
                  </ul>
                  <p style={{ marginTop: '15px', fontSize: '12px', color: '#666' }}>
                    Note: Email clients cannot automatically attach files from web pages for security reasons.
                  </p>
                </div>
              ),
              okText: 'OK'
            });
          }, 1500);
        }
        
        return;
        
      } catch (error) {
        console.error('Outlook protocol failed:', error);
        // Fallback to mailto:
      }
    }
    
    // Fallback to mailto:
    window.location.href = mailtoUrl;
    message.success('Opening default email client...');
  };

  // Handle Outlook Web sharing
  const handleOutlookWebShare = (articleSummary, fullTitle) => {
    const subject = `Knowledge Base: ${fullTitle.substring(0, 60)}${fullTitle.length > 60 ? '...' : ''}`;
    const body = articleSummary;
    const url = `https://outlook.office.com/mail/deeplink/compose?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    
    window.open(url, '_blank', 'noopener,noreferrer,width=800,height=600');
    message.success('Opening Outlook Web...');
  };

  // Handle Apple Mail
  const handleAppleMailShare = (articleSummary, fullTitle) => {
    const subject = `Knowledge Base: ${fullTitle.substring(0, 60)}${fullTitle.length > 60 ? '...' : ''}`;
    const body = articleSummary;
    const mailtoUrl = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    
    window.location.href = mailtoUrl;
    message.success('Opening Mail app...');
  };

  // Handle default mail client
  const handleDefaultMailShare = (articleSummary, fullTitle) => {
    const subject = `Knowledge Base: ${fullTitle.substring(0, 60)}${fullTitle.length > 60 ? '...' : ''}`;
    const body = articleSummary;
    const mailtoUrl = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    
    window.location.href = mailtoUrl;
    message.success('Opening default email client...');
  };

  // Special function to handle Yahoo share (avoid rate limiting)
  const handleYahooShare = (articleSummary, fullTitle) => {
    Modal.confirm({
      title: 'Share via Yahoo Mail',
      content: (
        <div>
          <p>To avoid rate limiting issues with Yahoo Mail:</p>
          <ol style={{ marginLeft: '20px', marginTop: '10px' }}>
            <li>Copy the article information below</li>
            <li>Open Yahoo Mail in your browser</li>
            <li>Compose a new email and paste the content</li>
            {attachments.length > 0 && (
              <li>Manually attach the files listed below</li>
            )}
          </ol>
          <div style={{ 
            marginTop: '15px',
            padding: '10px',
            background: '#f8f9fa',
            border: '1px solid #dee2e6',
            borderRadius: '5px',
            maxHeight: '200px',
            overflowY: 'auto',
            fontSize: '12px'
          }}>
            <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
              {`Subject: Knowledge Base: ${fullTitle.substring(0, 60)}${fullTitle.length > 60 ? '...' : ''}\n\n${articleSummary}`}
            </pre>
          </div>
          {attachments.length > 0 && (
            <div style={{ marginTop: '15px' }}>
              <p><strong>Files to attach:</strong></p>
              <ul style={{ marginLeft: '20px' }}>
                {attachments.map((att, index) => (
                  <li key={index}>
                    {att.original_name || att.filename || att.name}
                    {att.file_size ? ` (${formatFileSize(att.file_size)})` : ''}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      ),
      width: 500,
      okText: 'Copy to Clipboard',
      cancelText: 'Cancel',
      onOk() {
        navigator.clipboard.writeText(
          `Subject: Knowledge Base: ${fullTitle.substring(0, 60)}${fullTitle.length > 60 ? '...' : ''}\n\n${articleSummary}`
        ).then(() => {
          message.success('Email content copied! Open Yahoo Mail and paste.');
        }).catch(() => {
          message.error('Failed to copy. Please manually copy the text above.');
        });
      }
    });
  };

  // Special function to handle Gmail sharing
  const handleGmailShare = (articleSummary, fullTitle) => {
    // Gmail has issues with very long bodies
    const optimizedBody = articleSummary.length > 15000 
      ? articleSummary.substring(0, 15000) + '\n\n[Content truncated due to length limits. Visit link for full article.]'
      : articleSummary;
    
    const subject = `Knowledge Base: ${fullTitle.substring(0, 60)}${fullTitle.length > 60 ? '...' : ''}`;
    const url = `https://mail.google.com/mail/?view=cm&fs=1&su=${encodeURIComponent(subject)}&body=${encodeURIComponent(optimizedBody)}`;
    
    window.open(url, '_blank', 'noopener,noreferrer,width=800,height=600');
    
    // Show attachment instructions if needed
    if (attachments.length > 0) {
      setTimeout(() => {
        Modal.info({
          title: 'Attachments Note',
          content: (
            <div>
              <p>Please manually attach the following files in Gmail:</p>
              <ul style={{ marginLeft: '20px', marginTop: '10px' }}>
                {attachments.map((att, index) => (
                  <li key={index}>
                    {att.original_name || att.filename || att.name}
                    {att.file_size ? ` (${formatFileSize(att.file_size)})` : ''}
                  </li>
                ))}
              </ul>
            </div>
          ),
          okText: 'OK'
        });
      }, 1000);
    }
    
    message.success('Opening Gmail...');
  };

  // Email client selection menu
  const emailClientMenu = (
    <Menu>
      <Menu.Item key="default" onClick={() => handleShare('email', 'default')}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Mail size={16} />
          Default Email Client
          <span style={{ fontSize: '10px', color: '#666', marginLeft: 'auto' }}>
            (Auto-detect)
          </span>
        </div>
      </Menu.Item>
      <Menu.Divider />
      <Menu.Item key="outlook_desktop" onClick={() => handleShare('email', 'outlook_desktop')}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div style={{ width: '20px', height: '20px', background: '#0078D4', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Text style={{ color: 'white', fontSize: '10px', fontWeight: 'bold' }}>O</Text>
          </div>
          Outlook Desktop
        </div>
      </Menu.Item>
      <Menu.Item key="outlook_web" onClick={() => handleShare('email', 'outlook_web')}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div style={{ width: '20px', height: '20px', background: '#0072C6', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Text style={{ color: 'white', fontSize: '10px', fontWeight: 'bold' }}>O</Text>
          </div>
          Outlook Web
        </div>
      </Menu.Item>
      <Menu.Item key="gmail" onClick={() => handleShare('email', 'gmail')}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div style={{ width: '20px', height: '20px', background: '#EA4335', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Text style={{ color: 'white', fontSize: '10px', fontWeight: 'bold' }}>G</Text>
          </div>
          Gmail
        </div>
      </Menu.Item>
    </Menu>
  );

  // Enhanced print function with professional layout
  const handlePrint = () => {
    const printContent = document.querySelector('.kb-article-content')?.innerHTML || '';
    const currentUrl = window.location.href.replace('localhost', window.location.hostname);
    const articleUuid = article?.articleUuid || '';
    const fullTitle = articleUuid ? `[${articleUuid}] ${article?.title}` : article?.title;
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>${fullTitle} - Knowledge Base</title>
          <style>
            @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap');
            
            * {
              margin: 0;
              padding: 0;
              box-sizing: border-box;
            }
            
            body {
              font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
              line-height: 1.7;
              color: #1f2937;
              background: white;
              padding: 40px;
              max-width: 900px;
              margin: 0 auto;
            }
            
            .print-header {
              text-align: center;
              margin-bottom: 40px;
              padding-bottom: 30px;
              border-bottom: 2px solid #e5e7eb;
            }
            
            .print-header h1 {
              font-size: 28px;
              font-weight: 700;
              color: #111827;
              margin-bottom: 8px;
              line-height: 1.3;
            }
            
            .article-id {
              font-size: 14px;
              color: #6b7280;
              margin-bottom: 16px;
              font-family: 'SFMono-Regular', Consolas, monospace;
            }
            
            .print-meta {
              display: flex;
              justify-content: center;
              flex-wrap: wrap;
              gap: 20px;
              margin-top: 20px;
              font-size: 14px;
              color: #6b7280;
            }
            
            .print-meta-item {
              display: flex;
              align-items: center;
              gap: 6px;
            }
            
            .article-content {
              margin: 40px 0;
            }
            
            .article-content h1 {
              font-size: 24px;
              font-weight: 600;
              color: #111827;
              margin: 32px 0 16px 0;
              padding-bottom: 8px;
              border-bottom: 1px solid #e5e7eb;
            }
            
            .article-content h2 {
              font-size: 20px;
              font-weight: 600;
              color: #374151;
              margin: 28px 0 14px 0;
            }
            
            .article-content h3 {
              font-size: 18px;
              font-weight: 600;
              color: #4b5563;
              margin: 24px 0 12px 0;
            }
            
            .article-content p {
              margin: 16px 0;
              font-size: 15px;
              color: #4b5563;
            }
            
            .article-content code {
              background: #f3f4f6;
              padding: 2px 6px;
              border-radius: 4px;
              font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace;
              font-size: 14px;
              color: #dc2626;
            }
            
            .article-content pre {
              background: #1f2937;
              color: #f9fafb;
              padding: 20px;
              border-radius: 8px;
              overflow-x: auto;
              margin: 20px 0;
              font-size: 14px;
              line-height: 1.5;
            }
            
            .article-content blockquote {
              border-left: 4px solid #3b82f6;
              padding: 16px 24px;
              background: #f0f9ff;
              margin: 24px 0;
              border-radius: 0 8px 8px 0;
              font-style: italic;
            }
            
            .article-content img {
              max-width: 100%;
              height: auto;
              border-radius: 8px;
              margin: 20px 0;
              box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
            }
            
            .article-content ul, 
            .article-content ol {
              margin: 16px 0 16px 24px;
              color: #4b5563;
            }
            
            .article-content li {
              margin: 8px 0;
            }
            
            .print-footer {
              margin-top: 60px;
              padding-top: 30px;
              border-top: 1px solid #e5e7eb;
              text-align: center;
              font-size: 12px;
              color: #9ca3af;
              line-height: 1.6;
            }
            
            .print-footer p {
              margin: 8px 0;
              font-size: 12px;
            }
            
            .print-qr {
              margin: 20px auto;
              width: 120px;
              height: 120px;
              background: #f3f4f6;
              display: flex;
              alignItems: center;
              justifyContent: center;
              border-radius: 8px;
              color: #6b7280;
              font-size: 10px;
            }
            
            .article-attachments {
              margin: 30px 0;
              padding: 20px;
              background: #f9fafb;
              borderRadius: '8px',
              border-left: 4px solid #3b82f6;
            }
            
            .attachment-list {
              margin-top: 12px;
            }
            
            .attachment-item {
              display: flex;
              align-items: center;
              gap: 8px;
              margin: 8px 0;
              font-size: 14px;
              color: #4b5563;
            }
            
            @media print {
              body {
                padding: 20px;
              }
              
              .no-print {
                display: none;
              }
              
              .print-header {
                margin-bottom: 30px;
                padding-bottom: 20px;
              }
              
              .article-content h1 {
                page-break-after: avoid;
              }
              
              .article-content h2, 
              .article-content h3 {
                page-break-after: avoid;
              }
              
              .article-content img {
                page-break-inside: avoid;
              }
              
              .print-footer {
                margin-top: 40px;
                padding-top: 20px;
              }
            }
          </style>
        </head>
        <body>
          <div class="print-header">
            <div class="article-id">Article ID: ${articleUuid}</div>
            <h1>${article?.title}</h1>
            <p style="font-size: 16px; color: #6b7280; max-width: 600px; margin: 0 auto;">${article?.excerpt || ''}</p>
            
            <div class="print-meta">
              ${article?.categoryName ? `<div class="print-meta-item"><strong>Category:</strong> ${article.categoryName}</div>` : ''}
              ${article?.typeName ? `<div class="print-meta-item"><strong>Type:</strong> ${article.typeName}</div>` : ''}
              ${article?.authorName ? `<div class="print-meta-item"><strong>Author:</strong> ${article.authorName}</div>` : ''}
              <div class="print-meta-item"><strong>Published:</strong> ${formatDate(article?.publishedAt)}</div>
            </div>
          </div>
          
          ${attachments.length > 0 ? `
            <div class="article-attachments">
              <strong>📎 Article Attachments (${attachments.length}):</strong>
              <div class="attachment-list">
                ${attachments.map((att, index) => `
                  <div class="attachment-item">
                    ${index + 1}. ${att.original_name || att.filename || att.name} 
                    ${att.file_size ? `(${formatFileSize(att.file_size)})` : ''}
                  </div>
                `).join('')}
              </div>
            </div>
          ` : ''}
          
          <div class="article-content">
            ${printContent}
          </div>
          
          <div class="print-footer">
            <p><strong>Document Information</strong></p>
            <p>Article ID: ${articleUuid}</p>
            <p>Printed from YPS Helpdesk-Knowledge Base System on ${new Date().toLocaleDateString('en-US', { 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric',
              hour: '2-digit',
              minute: '2-digit'
            })}</p>
            <p>Article URL: ${currentUrl}</p>
            <p>Copyright © ${new Date().getFullYear()} YPS Helpdesk-Knowledge Base System. All rights reserved.</p>
            <div class="print-qr">
              <!-- QR Code placeholder -->
              <div style="text-align: center;">
                <div style="font-size: 10px; margin-bottom: 4px;">Scan to view online:</div>
                <div style="font-size: 8px; color: '#9ca3af';">[QR Code for: ${currentUrl}]</div>
              </div>
            </div>
          </div>
          
          <script>
            // Auto-print after content loads
            window.onload = function() {
              setTimeout(function() {
                window.print();
                setTimeout(function() {
                  window.close();
                }, 100);
              }, 250);
            };
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

// Word document download handler with client-side generation
const handleDownloadWord = () => {
  message.loading({ content: 'Generating Word document...', key: 'word', duration: 0 });
  
  try {
    const articleUuid = article?.articleUuid || '';
    const fullTitle = articleUuid ? `[${articleUuid}] ${article.title}` : article.title;
    
    // Get article content - use innerText to get clean text
    const articleContentElement = document.querySelector('.kb-article-content');
    let articleContent = '';
    
    if (articleContentElement) {
      // Get clean text from the rendered HTML
      articleContent = articleContentElement.innerText || articleContentElement.textContent || '';
    } else {
      // Fallback to article.content with HTML stripped
      articleContent = article?.content ? article.content.replace(/<[^>]*>/g, '') : '';
    }
    
    // Format the date
    const currentDate = new Date().toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
    
    // Create Word document content using simple HTML that can be opened in Word
    const wordContent = `
<!DOCTYPE html>
<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:w="urn:schemas-microsoft-com:office:word" xmlns="http://www.w3.org/TR/REC-html40">
<head>
  <meta charset="UTF-8">
  <meta name="ProgId" content="Word.Document">
  <meta name="Generator" content="Microsoft Word 15">
  <meta name="Originator" content="Microsoft Word 15">
  <title>${fullTitle} - Knowledge Base</title>
  <style>
    body {
      font-family: 'Calibri', 'Arial', sans-serif;
      line-height: 1.6;
      color: #000000;
      margin: 0;
      padding: 1in;
    }
    
    .header {
      text-align: center;
      margin-bottom: 40px;
      padding-bottom: 20px;
      border-bottom: 2px solid #0078D4;
    }
    
    h1 {
      font-size: 28pt;
      font-weight: bold;
      color: #0078D4;
      margin-bottom: 10px;
    }
    
    .article-id {
      font-size: 12pt;
      color: #666666;
      margin-bottom: 16px;
      font-family: 'Consolas', monospace;
    }
    
    .meta-info {
      font-size: 11pt;
      color: #666666;
      margin: 20px 0;
    }
    
    .meta-row {
      margin: 6px 0;
    }
    
    .content-section {
      margin: 40px 0;
    }
    
    .content-section h2 {
      font-size: 18pt;
      font-weight: bold;
      color: #2D3748;
      margin: 25px 0 15px 0;
      border-bottom: 1px solid #E2E8F0;
      padding-bottom: 5px;
    }
    
    .content-section h3 {
      font-size: 16pt;
      font-weight: bold;
      color: #4A5568;
      margin: 20px 0 12px 0;
    }
    
    .content-section p {
      margin: 12px 0;
      font-size: 11pt;
      color: #000000;
      text-align: justify;
    }
    
    .content-section ul, .content-section ol {
      margin: 12px 0 12px 20px;
    }
    
    .content-section li {
      margin: 6px 0;
      font-size: 11pt;
    }
    
    .content-section code {
      background: #F7FAFC;
      padding: 2px 4px;
      font-family: 'Consolas', monospace;
      font-size: 10pt;
      color: #C53030;
    }
    
    .content-section pre {
      background: #F7FAFC;
      border: 1px solid #E2E8F0;
      padding: 12px;
      margin: 15px 0;
      font-family: 'Consolas', monospace;
      font-size: 10pt;
      line-height: 1.4;
      overflow-x: auto;
    }
    
    .content-section blockquote {
      border-left: 4px solid #4299E1;
      padding: 12px 20px;
      background: #EBF8FF;
      margin: 20px 0;
      font-style: italic;
    }
    
    .footer {
      margin-top: 40px;
      padding-top: 20px;
      border-top: 1px solid #E2E8F0;
      text-align: center;
      font-size: 10pt;
      color: #666666;
      line-height: 1.6;
    }
    
    .attachments-section {
      margin: 30px 0;
      padding: 15px;
      background: #F7FAFC;
      border-left: 4px solid #4299E1;
    }
    
    .attachment-item {
      margin: 6px 0;
      font-size: 11pt;
    }
    
    .important-note {
      background: #FFF3CD;
      border-left: 4px solid #FFC107;
      padding: 12px;
      margin: 20px 0;
      font-size: 10pt;
    }
  </style>
</head>
<body>
  <div class="header">
    <div class="article-id">Article ID: ${articleUuid}</div>
    <h1>${article.title}</h1>
    ${article.excerpt ? `<p style="font-size: 14pt; color: #4A5568; max-width: 600px; margin: 0 auto;">${article.excerpt}</p>` : ''}
    
    <div class="meta-info">
      <div class="meta-row">
        <strong>Category:</strong> ${article.categoryName || 'N/A'}
      </div>
      <div class="meta-row">
        <strong>Type:</strong> ${article.typeName || 'N/A'}
      </div>
      ${article.authorName ? `<div class="meta-row"><strong>Author:</strong> ${article.authorName}</div>` : ''}
      <div class="meta-row">
        <strong>Published:</strong> ${formatDate(article.publishedAt)}
      </div>
      <div class="meta-row">
        <strong>Last Updated:</strong> ${formatDate(article.updatedAt)}
      </div>
    </div>
  </div>
  
  ${attachments.length > 0 ? `
    <div class="attachments-section">
      <h3>📎 Article Attachments (${attachments.length}):</h3>
      ${attachments.map((att, index) => `
        <div class="attachment-item">
          ${index + 1}. <strong>${att.original_name || att.filename || att.name || `Attachment_${index + 1}`}</strong>
          ${att.file_size ? ` (${formatFileSize(att.file_size)})` : ''}
        </div>
      `).join('')}
    </div>
  ` : ''}
  
  <div class="important-note">
    <strong>Note:</strong> This document was generated from the YPS Helpdesk-Knowledge Base System. For the latest version and updates, please refer to the online article.
  </div>
  
  <div class="content-section">
    <h2>Article Content</h2>
    ${articleContent.split('\n').map(line => `<p>${line}</p>`).join('')}
  </div>
  
  <div class="footer">
    <p><strong>Document Information</strong></p>
    <p>Article ID: ${articleUuid}</p>
    <p>Exported from YPS Helpdesk-Knowledge Base System on ${currentDate}</p>
    <p>Article URL: ${window.location.href}</p>
    <p>Copyright © ${new Date().getFullYear()} YPS Helpdesk-Knowledge Base System. All rights reserved.</p>
  </div>
</body>
</html>
    `;
    
    // Create a Blob with the content
    const blob = new Blob([wordContent], { 
      type: 'application/msword' 
    });
    
    // Create download link
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const fileName = articleUuid 
      ? `KB_${articleUuid}_${article.title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.doc`
      : `${article.title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_${Date.now()}.doc`;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    
    // Clean up
    setTimeout(() => {
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    }, 100);
    
    message.success({ 
      content: 'Word document downloaded successfully!', 
      key: 'word',
      duration: 3
    });
    
  } catch (error) {
    console.error('Error generating Word document:', error);
    message.destroy('word');
    
    // Simple fallback - copy to clipboard
    const articleContent = document.querySelector('.kb-article-content')?.innerText || 
                          article?.content?.replace(/<[^>]*>/g, '') || '';
    
    navigator.clipboard.writeText(`Article: ${article.title}\n\n${articleContent}`)
      .then(() => {
        message.success('Article content copied to clipboard! Paste into Word.');
      })
      .catch(() => {
        message.error('Failed to generate Word document. Please try manual copy.');
      });
  }
};

  // Format file size
  const formatFileSize = (bytes) => {
    if (!bytes || bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Download attachment
  const handleDownloadAttachment = async (attachment) => {
    try {
      // Try different attachment URL patterns
      const attachmentId = attachment.id || attachment.attachment_uuid;
      const fileName = attachment.original_name || attachment.filename || attachment.name || 'attachment';
      const fileUrl = attachment.file_url || attachment.url;
      
      let downloadUrl = fileUrl;
      
      if (!downloadUrl && attachmentId) {
        // Construct download URL based on backend API
        downloadUrl = `${window.location.origin}/api/attachments/download/${attachmentId}`;
      }
      
      if (!downloadUrl) {
        message.error('Download URL not available for this attachment');
        return;
      }
      
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.setAttribute('download', fileName);
      link.setAttribute('target', '_blank');
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      message.success(`Downloading: ${fileName}`);
    } catch (error) {
      console.error('Error downloading attachment:', error);
      message.error('Failed to download attachment');
    }
  };

  // Support status modal with improved layout
  const SupportStatusModal = () => (
    <Modal
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <HelpCircle size={22} color="#4f46e5" />
          <span style={{ fontSize: '18px', fontWeight: 600 }}>Support Ticket Status</span>
        </div>
      }
      open={isSupportModalVisible}
      onCancel={() => setIsSupportModalVisible(false)}
      footer={[
        <Button key="close" onClick={() => setIsSupportModalVisible(false)} style={{ padding: '8px 20px' }}>
          Close
        </Button>,
        supportTicket && (
          <Button 
            key="view" 
            type="primary"
            onClick={() => {
              setIsSupportModalVisible(false);
              navigate(`/tickets/${article.ticketId || article.ticketUuid}`);
            }}
            style={{ padding: '8px 20px' }}
          >
            View Full Ticket
          </Button>
        )
      ]}
      width={600}
      style={{ top: 40 }}
    >
      {supportLoading ? (
        <div style={{ textAlign: 'center', padding: '40px 20px' }}>
          <Spin size="large" />
          <div style={{ marginTop: '20px', color: '#6b7280', fontSize: '15px' }}>
            Loading ticket details...
          </div>
        </div>
      ) : supportTicket ? (
        <div className="support-ticket-details" style={{ padding: '10px 0' }}>
          {/* Ticket Header */}
          <div style={{ 
            marginBottom: '24px',
            padding: '16px',
            background: '#f8fafc',
            borderRadius: '8px',
            borderLeft: '4px solid #4f46e5'
          }}>
            <Title level={5} style={{ marginBottom: '8px', color: '#1f2937' }}>
              {supportTicket.subject || 'Support Ticket'}
            </Title>
            <Text type="secondary" style={{ fontSize: '14px', lineHeight: '1.6' }}>
              {supportTicket.description?.substring(0, 200) || 'No description provided.'}
              {supportTicket.description?.length > 200 ? '...' : ''}
            </Text>
          </div>
          
          {/* Ticket Details Grid */}
          <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
            <Col xs={24} sm={12}>
              <div style={{ 
                padding: '16px',
                background: '#f9fafb',
                borderRadius: '8px',
                border: '1px solid #e5e7eb'
              }}>
                <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: '6px' }}>
                  Ticket ID
                </Text>
                <Text strong style={{ fontSize: '15px', color: '#1f2937' }}>
                  {supportTicket.ticketUuid || supportTicket.ticketNumber || supportTicket.id || 'N/A'}
                </Text>
              </div>
            </Col>
            
            <Col xs={24} sm={12}>
              <div style={{ 
                padding: '16px',
                background: '#f9fafb',
                borderRadius: '8px',
                border: '1px solid #e5e7eb'
              }}>
                <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: '6px' }}>
                  Status
                </Text>
                <div>
                  <Badge 
                    status={
                      supportTicket.status === 'closed' || supportTicket.status === 'resolved' ? 'success' :
                      supportTicket.status === 'open' ? 'error' :
                      supportTicket.status === 'in progress' || supportTicket.status === 'pending' ? 'warning' : 'default'
                    }
                    text={
                      <Text strong style={{ 
                        fontSize: '14px',
                        color: '#1f2937',
                        marginLeft: '8px'
                      }}>
                        {supportTicket.statusName || 
                          (supportTicket.status ? supportTicket.status.charAt(0).toUpperCase() + supportTicket.status.slice(1) : 'Unknown')
                        }
                      </Text>
                    }
                  />
                </div>
              </div>
            </Col>
            
            <Col xs={24} sm={12}>
              <div style={{ 
                padding: '16px',
                background: '#f9fafb',
                borderRadius: '8px',
                border: '1px solid #e5e7eb'
              }}>
                <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: '6px' }}>
                  Priority
                </Text>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <div style={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    backgroundColor: 
                      supportTicket.priority === 'high' || supportTicket.priorityName?.toLowerCase() === 'high' ? '#ef4444' :
                      supportTicket.priority === 'medium' || supportTicket.priorityName?.toLowerCase() === 'medium' ? '#f59e0b' :
                      supportTicket.priority === 'low' || supportTicket.priorityName?.toLowerCase() === 'low' ? '#10b981' : '#6b7280'
                  }} />
                  <Text strong style={{ fontSize: '14px', color: '#1f2937' }}>
                    {supportTicket.priorityName || 
                      (supportTicket.priority ? supportTicket.priority.charAt(0).toUpperCase() + supportTicket.priority.slice(1) : 'Normal')
                    }
                  </Text>
                </div>
              </div>
            </Col>
            
            <Col xs={24} sm={12}>
              <div style={{ 
                padding: '16px',
                background: '#f9fafb',
                borderRadius: '8px',
                border: '1px solid #e5e7eb'
              }}>
                <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: '6px' }}>
                  Created Date
                </Text>
                <Text strong style={{ fontSize: '14px', color: '#1f2937' }}>
                  {supportTicket.caseReportedDate ? formatDate(supportTicket.caseReportedDate) : 
                   supportTicket.createdAt ? formatDate(supportTicket.createdAt) : 'N/A'}
                </Text>
              </div>
            </Col>
            
            {/* Display assigned agent if available */}
            {supportTicket.assignedTo && supportTicket.assignedTo !== 'Unassigned' && (
              <Col span={24}>
                <div style={{ 
                  padding: '16px',
                  background: '#f9fafb',
                  borderRadius: '8px',
                  border: '1px solid #e5e7eb'
                }}>
                  <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: '6px' }}>
                    Assigned To
                  </Text>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div style={{
                      width: '32px',
                      height: '32px',
                      borderRadius: '50%',
                      background: '#4f46e5',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      fontWeight: 'bold',
                      fontSize: '14px'
                    }}>
                      {supportTicket.assignedTo.charAt(0).toUpperCase()}
                    </div>
                    <Text strong style={{ fontSize: '15px', color: '#1f2937' }}>
                      {supportTicket.assignedTo}
                    </Text>
                  </div>
                </div>
              </Col>
            )}
            
            {/* Display organization if available */}
            {supportTicket.organizationName && (
              <Col xs={24} sm={12}>
                <div style={{ 
                  padding: '16px',
                  background: '#f9fafb',
                  borderRadius: '8px',
                  border: '1px solid #e5e7eb'
                }}>
                  <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: '6px' }}>
                    Organization
                  </Text>
                  <Text strong style={{ fontSize: '14px', color: '#1f2937' }}>
                    {supportTicket.organizationName}
                  </Text>
                </div>
              </Col>
            )}
            
            {/* Display category if available */}
            {supportTicket.category && (
              <Col xs={24} sm={12}>
                <div style={{ 
                  padding: '16px',
                  background: '#f9fafb',
                  borderRadius: '8px',
                  border: '1px solid #e5e7eb'
                }}>
                  <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: '6px' }}>
                    Category
                  </Text>
                  <Text strong style={{ fontSize: '14px', color: '#1f2937' }}>
                    {supportTicket.category}
                  </Text>
                </div>
              </Col>
            )}
          </Row>

          {/* Resolution status if resolved or closed */}
          {(supportTicket.status === 'resolved' || supportTicket.status === 'closed') && supportTicket.resolvedAt && (
            <>
              <Divider style={{ margin: '20px 0' }} />
              <div>
                <Text strong style={{ fontSize: '15px', color: '#1f2937', marginBottom: '12px', display: 'block' }}>
                  Resolution Status
                </Text>
                <div style={{ 
                  background: '#d1fae5', 
                  padding: '16px', 
                  borderRadius: '8px',
                  fontSize: '14px',
                  lineHeight: '1.6',
                  color: '#065f46'
                }}>
                  <div style={{ marginBottom: '8px' }}>
                    <strong>Resolved on:</strong> {formatDate(supportTicket.resolvedAt)}
                  </div>
                  {supportTicket.resolutionTaken && (
                    <div style={{ marginTop: '8px' }}>
                      <strong>Resolution:</strong> {supportTicket.resolutionTaken.substring(0, 150)}
                      {supportTicket.resolutionTaken.length > 150 ? '...' : ''}
                    </div>
                  )}
                </div>
              </div>
            </>
          )}

          {/* Due date warning if ticket is still open and overdue */}
          {supportTicket.dueDate && (supportTicket.status === 'open' || supportTicket.status === 'pending' || supportTicket.status === 'in progress') && (
            <>
              <Divider style={{ margin: '20px 0' }} />
              <div>
                <Text strong style={{ fontSize: '15px', color: '#1f2937', marginBottom: '12px', display: 'block' }}>
                  Due Date
                </Text>
                <div style={{ 
                  background: new Date(supportTicket.dueDate) < new Date() ? '#fee2e2' : '#fef3c7', 
                  padding: '16px', 
                  borderRadius: '8px',
                  fontSize: '14px',
                  lineHeight: '1.6',
                  color: new Date(supportTicket.dueDate) < new Date() ? '#991b1b' : '#92400e'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span>
                      <strong>Due:</strong> {formatDate(supportTicket.dueDate)}
                    </span>
                    {new Date(supportTicket.dueDate) < new Date() && (
                      <Badge count="Overdue" style={{ backgroundColor: '#ef4444' }} />
                    )}
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Latest Update */}
          {supportTicket.resolutionTaken || (supportTicket.comments && supportTicket.comments.length > 0) ? (
            <>
              <Divider style={{ margin: '20px 0' }} />
              <div>
                <Text strong style={{ fontSize: '15px', color: '#1f2937', marginBottom: '12px', display: 'block' }}>
                  Latest Update
                </Text>
                <div style={{ 
                  background: '#f0f9ff', 
                  padding: '16px', 
                  borderRadius: '8px',
                  fontSize: '14px',
                  lineHeight: '1.6',
                  color: '#1e40af'
                }}>
                  {supportTicket.resolutionTaken ? (
                    <div>
                      <div style={{ marginBottom: '8px', fontWeight: 500 }}>
                        📝 Resolution:
                      </div>
                      <div>
                        {supportTicket.resolutionTaken.substring(0, 200)}
                        {supportTicket.resolutionTaken.length > 200 ? '...' : ''}
                      </div>
                      {supportTicket.resolvedAt && (
                        <div style={{ marginTop: '8px', fontSize: '13px', opacity: 0.8 }}>
                          Resolved on: {formatDate(supportTicket.resolvedAt)}
                        </div>
                      )}
                    </div>
                  ) : supportTicket.comments && supportTicket.comments.length > 0 ? (
                    (() => {
                      // Get the latest non-system comment (system comments usually don't have user info)
                      const latestComment = [...supportTicket.comments]
                        .filter(comment => !comment.isSystemEvent && comment.content && comment.content.trim() !== '')
                        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))[0];
                      
                      // If no non-system comments, get the latest comment
                      const displayComment = latestComment || supportTicket.comments[supportTicket.comments.length - 1];
                      
                      if (!displayComment || !displayComment.content) {
                        return 'No comments available';
                      }
                      
                      return (
                        <div>
                          <div style={{ marginBottom: '8px', fontWeight: 500 }}>
                            {displayComment.isSystemEvent ? '📋 System Event' : '💬 Latest Comment'} 
                            ({supportTicket.comments.length} total):
                          </div>
                          <div style={{ marginBottom: '8px', fontStyle: displayComment.isSystemEvent ? 'italic' : 'normal' }}>
                            {displayComment.content.substring(0, 200)}
                            {displayComment.content.length > 200 ? '...' : ''}
                          </div>
                          <div style={{ fontSize: '12px', color: '#3b82f6', display: 'flex', justifyContent: 'space-between' }}>
                            <span>
                              {displayComment.isSystemEvent ? 'System' : 
                                `By: ${displayComment.userName || displayComment.userFirstName || 
                                  displayComment.userEmail || 'Unknown'}`}
                            </span>
                            <span>
                              {displayComment.createdAt ? formatDate(displayComment.createdAt) : ''}
                            </span>
                          </div>
                        </div>
                      );
                    })()
                  ) : 'No updates available'}
                </div>
              </div>
            </>
          ) : null}
        </div>
      ) : (
        <Empty
          description={
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '16px', color: '#6b7280', marginBottom: '8px' }}>
                No ticket information available
              </div>
              <Text type="secondary" style={{ fontSize: '14px' }}>
                The linked support ticket could not be found or you may not have access to view it.
              </Text>
            </div>
          }
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          imageStyle={{ height: 80, marginBottom: 20 }}
        />
      )}
    </Modal>
  );

  if (loading) {
    return (
      <div className="kb-page">
        <div className="kb-main-card">
          <div className="kb-loading-container">
            <Spin size="large" />
            <div style={{ marginTop: '16px', color: '#718096', fontSize: '16px' }}>
              Loading article...
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="kb-page">
        <div className="kb-main-card">
          <Empty
            description={
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '20px', color: '#2d3748', marginBottom: '12px', fontWeight: 600 }}>
                  Article not found
                </div>
                <div style={{ fontSize: '15px', color: '#718096', maxWidth: '400px', margin: '0 auto' }}>
                  The article you are looking for does not exist or has been deleted.
                </div>
              </div>
            }
            imageStyle={{ height: 140, marginBottom: 24 }}
          >
            <Button 
              type="primary" 
              size="large"
              onClick={() => navigate('/knowledge-base')}
              style={{ padding: '12px 32px', height: '48px', borderRadius: '12px' }}
            >
              <ArrowLeft size={16} style={{ marginRight: '8px' }} />
              Back to Knowledge Base
            </Button>
          </Empty>
        </div>
      </div>
    );
  }

  const priorityInfo = getPriorityInfo(article.priorityId);
  const statusInfo = getStatusInfo(article.status);
  const typeInfo = getTypeInfo(article.typeName);
  const articleUuid = article?.articleUuid || '';
  const fullTitle = articleUuid ? `[${articleUuid}] ${article.title}` : article.title;

  return (
    <div className="kb-page">
      {/* Header */}
      <div className="kb-header">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div style={{ maxWidth: '800px' }}>
            <Button
              type="text"
              icon={<ArrowLeft size={20} />}
              onClick={() => navigate('/knowledge-base')}
              className="kb-back-button"
            >
              Back to Knowledge Base
            </Button>
            <div style={{ 
              marginBottom: '8px', 
              fontSize: '15px', 
              color: '#f0f9ff', 
              fontFamily: "'SF Mono', 'Monaco', 'Inconsolata', 'Fira Mono', monospace",
              fontWeight: '500',
              letterSpacing: '0.5px',
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              padding: '6px 12px',
              borderRadius: '6px',
              display: 'inline-block'
            }}>
              Article ID: {articleUuid}
            </div>
            <Title level={1} className="kb-title" style={{ fontSize: '32px', marginTop: '0' }}>
              {article.title}
            </Title>
            <Text className="kb-description" style={{ fontSize: '17px', lineHeight: '1.7' }}>
              {article.excerpt || 'No description available'}
            </Text>
          </div>
        </div>
      </div>

      <div className="kb-main-card">
        <div className="kb-content">
          <Row gutter={[32, 32]}>
            {/* Main Content */}
            <Col xs={24} lg={16}>
              <Card className="kb-detail-section">
                {/* Article Meta Header */}
                <div className="kb-article-meta-header">
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                    {article.categoryName && (
                      <Badge 
                        className="kb-article-category"
                        style={{ 
                          backgroundColor: article.categoryName === 'Hardware' ? '#dbeafe' :
                                        article.categoryName === 'Software' ? '#dcfce7' :
                                        article.categoryName === 'Network' ? '#ffe4e6' : 
                                        article.categoryName === 'Security' ? '#f3e8ff' : '#f1f5f9',
                          color: article.categoryName === 'Hardware' ? '#1e40af' :
                               article.categoryName === 'Software' ? '#166534' :
                               article.categoryName === 'Network' ? '#9f1239' : 
                               article.categoryName === 'Security' ? '#6b21a8' : '#374151',
                          padding: '8px 16px',
                          borderRadius: '20px',
                          fontWeight: 600,
                          fontSize: '13px'
                        }}
                      >
                        {article.categoryName}
                      </Badge>
                    )}
                    
                    {article.typeName && (
                      <span className="kb-article-type" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span style={{ fontSize: '14px' }}>{typeInfo.icon}</span>
                        <span style={{ fontWeight: 500, fontSize: '13px' }}>{article.typeName}</span>
                      </span>
                    )}
                    
                    {article.priorityId && (
                      <span 
                        className="kb-priority-badge"
                        style={{
                          backgroundColor: priorityInfo.bgColor,
                          color: priorityInfo.color,
                          padding: '8px 16px',
                          borderRadius: '20px',
                          fontSize: '13px',
                          fontWeight: 500,
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px'
                        }}
                      >
                        <AlertCircle size={14} />
                        {priorityInfo.label} Priority
                      </span>
                    )}
                    
                    {article.ticketUuid && (
                      <Tooltip title={`View linked ticket: ${article.ticketUuid}`}>
                        <span 
                          className="kb-ticket-link"
                          onClick={() => navigate(`/tickets/${article.ticketId || article.ticketUuid}`)}
                          style={{ cursor: 'pointer' }}
                        >
                          <LinkIcon size={14} />
                          Ticket: {article.ticketUuid}
                        </span>
                      </Tooltip>
                    )}
                  </div>
                </div>

                {/* Article Stats */}
                <div className="kb-article-stats">
                  <div style={{ display: 'flex', alignItems: 'center', gap: '24px', flexWrap: 'wrap' }}>
                    <span className="kb-stat-item">
                      <ThumbsUp size={16} style={{ color: '#059669' }} />
                      <Text strong style={{ fontSize: '15px', color: '#059669' }}>{article.helpfulCount || 0}</Text>
                      <Text type="secondary" style={{ fontSize: '13px' }}>helpful</Text>
                    </span>
                    
                    <span className="kb-stat-item">
                      <ThumbsDown size={16} style={{ color: '#dc2626' }} />
                      <Text strong style={{ fontSize: '15px', color: '#dc2626' }}>{article.notHelpfulCount || 0}</Text>
                      <Text type="secondary" style={{ fontSize: '13px' }}>not helpful</Text>
                    </span>
                    
                    {article.productName && (
                      <span className="kb-stat-item">
                        <Package size={16} style={{ color: '#7c3aed' }} />
                        <Text strong style={{ fontSize: '15px' }}>{article.productName}</Text>
                      </span>
                    )}
                  </div>
                </div>

                {/* Tags Section */}
                {article.tags && article.tags.length > 0 && (
                  <div className="kb-article-tags-container">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                      <TagIcon size={16} style={{ color: '#4b5563' }} />
                      <Text strong style={{ fontSize: '14px' }}>Tags</Text>
                    </div>
                    <Space size={[8, 8]} wrap>
                      {article.tags.map(tag => (
                        <span key={tag} className="kb-article-tag">
                          <TagIcon size={12} style={{ marginRight: '6px' }} />
                          {tag}
                        </span>
                      ))}
                    </Space>
                  </div>
                )}

                {/* Attachments Section */}
                {attachments.length > 0 && (
                  <div className="kb-article-attachments" style={{ marginTop: '24px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                      <Paperclip size={18} style={{ color: '#4b5563' }} />
                      <Text strong style={{ fontSize: '15px' }}>Attachments ({attachments.length})</Text>
                    </div>
                    <List
                      size="small"
                      dataSource={attachments}
                      renderItem={(attachment, index) => (
                        <List.Item
                          key={attachment.id || attachment.attachment_uuid || index}
                          style={{ 
                            padding: '12px 16px',
                            border: '1px solid #e5e7eb',
                            borderRadius: '8px',
                            marginBottom: '8px',
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            background: 'white'
                          }}
                          onClick={() => handleDownloadAttachment(attachment)}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                        >
                          <List.Item.Meta
                            avatar={
                              <div style={{ 
                                width: '36px', 
                                height: '36px', 
                                background: '#f3f4f6',
                                borderRadius: '8px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}>
                                <File size={18} color="#6b7280" />
                              </div>
                            }
                            title={
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                                <Text strong style={{ fontSize: '14px' }}>
                                  {attachment.original_name || attachment.filename || attachment.name || `Attachment ${index + 1}`}
                                </Text>
                                {attachment.file_size && (
                                  <Text type="secondary" style={{ fontSize: '12px' }}>
                                    ({formatFileSize(attachment.file_size)})
                                  </Text>
                                )}
                              </div>
                            }
                            description={
                              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '4px' }}>
                                <Text type="secondary" style={{ fontSize: '12px' }}>
                                  {attachment.mime_type || 'File'} • 
                                  {attachment.uploaded_at ? ` Uploaded ${formatDate(attachment.uploaded_at)}` : ''}
                                </Text>
                              </div>
                            }
                          />
                          <Download size={16} color="#6b7280" />
                        </List.Item>
                      )}
                    />
                  </div>
                )}

                {/* Article Info */}
                <div className="kb-article-info">
                  <Space size="large" wrap>
                    {article.authorName && (
                      <span className="kb-info-item">
                        <User size={16} style={{ color: '#4b5563' }} />
                        <div>
                          <Text type="secondary" style={{ fontSize: '12px', display: 'block' }}>Author</Text>
                          <Text strong style={{ fontSize: '14px' }}>{article.authorName}</Text>
                        </div>
                      </span>
                    )}
                    <span className="kb-info-item">
                      <Calendar size={16} style={{ color: '#4b5563' }} />
                      <div>
                        <Text type="secondary" style={{ fontSize: '12px', display: 'block' }}>Published</Text>
                        <Text strong style={{ fontSize: '14px' }}>{formatDate(article.publishedAt)}</Text>
                      </div>
                    </span>
                    <span className="kb-info-item">
                      <Clock size={16} style={{ color: '#4b5563' }} />
                      <div>
                        <Text type="secondary" style={{ fontSize: '12px', display: 'block' }}>Last Updated</Text>
                        <Text strong style={{ fontSize: '14px' }}>{formatDate(article.updatedAt)}</Text>
                      </div>
                    </span>
                  </Space>
                </div>

                <Divider style={{ margin: '32px 0', borderColor: '#e5e7eb' }} />

                {/* Article Content */}
                <div 
                  className="kb-article-content"
                  dangerouslySetInnerHTML={{ __html: parseMarkdownContent(article.content) }}
                />

                <Divider style={{ margin: '40px 0', borderColor: '#e5e7eb' }} />

                {/* Feedback Section */}
                <div className="kb-article-feedback">
                  <div style={{ textAlign: 'center', marginBottom: '32px' }}>
                    <Title level={4} style={{ marginBottom: '8px', color: '#1f2937' }}>
                      Was this article helpful?
                    </Title>
                    <Paragraph type="secondary" style={{ marginBottom: 0, fontSize: '15px', color: '#6b7280' }}>
                      Your feedback helps us improve our knowledge base content for everyone.
                    </Paragraph>
                  </div>
                  
                  <div style={{ display: 'flex', justifyContent: 'center', gap: '16px', flexWrap: 'wrap' }}>
                    <Button 
                      type="primary" 
                      icon={<ThumbsUp size={18} />}
                      onClick={() => handleHelpfulVote(true)}
                      className="kb-feedback-button"
                      size="large"
                      loading={helpfulLoading}
                      style={{ 
                        minWidth: '160px',
                        background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                        border: 'none',
                        height: '48px'
                      }}
                    >
                      Yes, it helped
                    </Button>
                    <Button 
                      icon={<ThumbsDown size={18} />}
                      onClick={() => handleHelpfulVote(false)}
                      className="kb-feedback-button"
                      size="large"
                      loading={notHelpfulLoading}
                      style={{ 
                        minWidth: '160px',
                        background: 'white',
                        border: '2px solid #e5e7eb',
                        color: '#4b5563',
                        height: '48px'
                      }}
                    >
                      Could be better
                    </Button>
                  </div>
                  
                  <div style={{ textAlign: 'center', marginTop: '24px' }}>
                    <Text type="secondary" style={{ fontSize: '13px', color: '#9ca3af' }}>
                      {(article.helpfulCount || 0) + (article.notHelpfulCount || 0)} people have voted • 
                      {((article.helpfulCount || 0) / ((article.helpfulCount || 0) + (article.notHelpfulCount || 0) || 1) * 100).toFixed(0)}% found this helpful
                    </Text>
                  </div>
                </div>
              </Card>

              {/* Related Articles */}
              {relatedArticles.length > 0 && (
                <Card className="kb-form-section" style={{ marginTop: '24px' }}>
                  <div className="kb-section-title">
                    <BookOpen size={18} />
                    Related Articles
                  </div>
                  <div className="kb-related-articles">
                    {relatedArticles.map(related => (
                      <React.Fragment key={related.id}>
                        <div 
                          className="kb-related-article"
                          onClick={() => navigate(`/knowledge-base/${related.id || related.articleUuid}`)}
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <div>
                              <Text strong style={{ fontSize: '15px', display: 'block', marginBottom: '4px' }}>
                                {related.title}
                              </Text>
                              <Text type="secondary" style={{ fontSize: '13px' }}>
                                {related.excerpt?.substring(0, 100)}...
                              </Text>
                            </div>
                            <ExternalLink size={14} style={{ color: '#9ca3af', marginLeft: '12px' }} />
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '8px' }}>
                            {related.categoryName && (
                              <span style={{ fontSize: '12px', color: '#6b7280' }}>
                                {related.categoryName}
                              </span>
                            )}
                          </div>
                        </div>
                        <Divider style={{ margin: '12px 0' }} />
                      </React.Fragment>
                    ))}
                  </div>
                </Card>
              )}
            </Col>

            {/* Sidebar */}
            <Col xs={24} lg={8}>
              {/* Article Actions */}
              <Card className="kb-form-section">
                <div className="kb-section-title">
                  <FileText size={18} />
                  Article Actions
                </div>
                
                <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                  <Button 
                    icon={<Edit size={16} />} 
                    onClick={() => navigate(`/knowledge-base/edit/${article.id || article.articleUuid}`)}
                    className="kb-action-button"
                    block
                    size="large"
                  >
                    Edit Article
                  </Button>
                  
                  <Button 
                    icon={<Download size={16} />}
                    onClick={handleDownloadWord}
                    className="kb-action-button"
                    block
                    size="large"
                  >
                    Download as Word
                  </Button>
                  
                  <Button 
                    icon={<Printer size={16} />}
                    onClick={handlePrint}
                    className="kb-action-button"
                    block
                    size="large"
                  >
                    Print Article
                  </Button>
                  
                  {/* Check Support Status Button */}
                  {article.ticketId || article.ticketUuid ? (
                    <Button 
                      icon={<HelpCircle size={16} />}
                      onClick={fetchSupportTicket}
                      className="kb-action-button"
                      block
                      size="large"
                      loading={supportLoading}
                      style={{
                        background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                        border: 'none',
                        color: 'white'
                      }}
                    >
                      Check Support Status
                    </Button>
                  ) : null}
                  
                  {/* Share Article Button */}
                  <div style={{ position: 'relative' }}>
                    <Button 
                      icon={<Share2 size={16} />}
                      onClick={() => setShowShareOptions(!showShareOptions)}
                      className="kb-action-button"
                      block
                      size="large"
                    >
                      Share Article
                    </Button>
                    
                    {showShareOptions && (
                      <div style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        background: 'white',
                        border: '1px solid #e5e7eb',
                        borderRadius: '12px',
                        marginTop: '8px',
                        padding: '16px',
                        boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)',
                        zIndex: 100
                      }}>
                        <div style={{ marginBottom: '12px', color: '#4b5563', fontSize: '14px', fontWeight: 500 }}>
                          Share Options:
                        </div>
                        
                        {/* Copy to Clipboard - Primary Option */}
                        <Button 
                          size="middle" 
                          onClick={() => handleShare('copy')}
                          style={{ 
                            width: '100%',
                            marginBottom: '8px',
                            background: '#3b82f6',
                            borderColor: '#3b82f6',
                            color: 'white',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '8px',
                            padding: '10px'
                          }}
                        >
                          <FileText size={16} />
                          Copy Article Info
                        </Button>
                        
                        <div style={{ 
                          display: 'grid', 
                          gridTemplateColumns: '1fr 1fr', 
                          gap: '8px',
                          marginBottom: '12px'
                        }}>
                          {/* WhatsApp */}
                          <Button 
                            size="small"
                            onClick={() => handleShare('whatsapp')}
                            style={{ 
                              background: '#25D366', 
                              borderColor: '#25D366',
                              color: 'white',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: '6px',
                              padding: '8px'
                            }}
                          >
                            <span style={{ fontSize: '14px' }}>WhatsApp</span>
                          </Button>
                          
                          {/* Email Dropdown */}
                          <Dropdown overlay={emailClientMenu} trigger={['click']} placement="bottomLeft">
                            <Button 
                              size="small"
                              style={{ 
                                width: '100%',
                                background: '#f3f4f6',
                                borderColor: '#e5e7eb',
                                color: '#4b5563',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '6px',
                                padding: '8px'
                              }}
                            >
                              <Mail size={14} />
                              <span>Email</span>
                            </Button>
                          </Dropdown>
                        </div>
                        
                        {/* Attachment Information */}
                        {attachments.length > 0 && (
                          <div style={{ 
                            marginTop: '12px',
                            padding: '10px',
                            background: '#f0f9ff',
                            borderRadius: '6px',
                            fontSize: '11px',
                            color: '#0c4a6e',
                            borderLeft: '3px solid #0ea5e9'
                          }}>
                            <div style={{ fontWeight: 500, marginBottom: '4px' }}>📎 Note about attachments:</div>
                            <div>Email clients cannot automatically attach files from web pages.</div>
                            <div>You'll need to manually attach {attachments.length} file(s) after opening email.</div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </Space>
              </Card>

              {/* Article Details */}
              <Card className="kb-form-section" style={{ marginTop: '24px' }}>
                <div className="kb-section-title">
                  <TagIcon size={18} />
                  Article Details
                </div>
                
                <div className="kb-details-grid">
                  <div className="kb-detail-item">
                    <Text type="secondary" className="kb-detail-label">Status</Text>
                    <div className="kb-detail-value">
                      <span 
                        className="kb-status-badge"
                        style={{
                          backgroundColor: statusInfo.bgColor,
                          color: statusInfo.color,
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '6px',
                          padding: '6px 12px',
                          borderRadius: '20px',
                          fontSize: '12px',
                          fontWeight: 600
                        }}
                      >
                        {statusInfo.icon}
                        {statusInfo.label}
                      </span>
                    </div>
                  </div>

                  <div className="kb-detail-item">
                    <Text type="secondary" className="kb-detail-label">Article ID</Text>
                    <div className="kb-detail-value">
                      <Text code style={{ fontSize: '12px', background: '#f3f4f6', padding: '4px 8px', borderRadius: '6px' }}>
                        {article.articleUuid}
                      </Text>
                    </div>
                  </div>

                  {article.categoryName && (
                    <div className="kb-detail-item">
                      <Text type="secondary" className="kb-detail-label">Category</Text>
                      <div className="kb-detail-value">{article.categoryName}</div>
                    </div>
                  )}

                  {article.typeName && (
                    <div className="kb-detail-item">
                      <Text type="secondary" className="kb-detail-label">Type</Text>
                      <div className="kb-detail-value" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ fontSize: '14px' }}>{typeInfo.icon}</span>
                        <span>{article.typeName}</span>
                      </div>
                    </div>
                  )}

                  {article.productName && (
                    <div className="kb-detail-item">
                      <Text type="secondary" className="kb-detail-label">Product</Text>
                      <div className="kb-detail-value" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Package size={14} style={{ color: typeInfo.color }} />
                        {article.productName}
                      </div>
                    </div>
                  )}

                  {article.priorityId && (
                    <div className="kb-detail-item">
                      <Text type="secondary" className="kb-detail-label">Priority</Text>
                      <div className="kb-detail-value">
                        <span 
                          className="kb-priority-badge"
                          style={{
                            backgroundColor: priorityInfo.bgColor,
                            color: priorityInfo.color,
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px',
                            padding: '6px 12px',
                            borderRadius: '20px',
                            fontSize: '12px',
                            fontWeight: 500
                          }}
                        >
                          <AlertCircle size={12} />
                          {priorityInfo.label}
                        </span>
                      </div>
                    </div>
                  )}

                  {article.ticketUuid && (
                    <div className="kb-detail-item">
                      <Text type="secondary" className="kb-detail-label">Linked Ticket</Text>
                      <div className="kb-detail-value" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <LinkIcon size={14} style={{ color: typeInfo.color }} />
                        <a 
                          href={`/tickets/${article.ticketId || article.ticketUuid}`}
                          onClick={(e) => {
                            e.preventDefault();
                            navigate(`/tickets/${article.ticketId || article.ticketUuid}`);
                          }}
                          style={{ color: typeInfo.color, textDecoration: 'none' }}
                        >
                          {article.ticketUuid}
                        </a>
                      </div>
                    </div>
                  )}

                  <div className="kb-detail-item">
                    <Text type="secondary" className="kb-detail-label">Created</Text>
                    <div className="kb-detail-value">
                      {formatDate(article.createdAt)}
                    </div>
                  </div>

                  <div className="kb-detail-item">
                    <Text type="secondary" className="kb-detail-label">Last Updated</Text>
                    <div className="kb-detail-value">
                      {formatDate(article.updatedAt)}
                    </div>
                  </div>

                  <div className="kb-detail-item">
                    <Text type="secondary" className="kb-detail-label">Published</Text>
                    <div className="kb-detail-value">
                      {article.publishedAt 
                        ? formatDate(article.publishedAt)
                        : <Text type="secondary">Not published</Text>}
                    </div>
                  </div>
                </div>
              </Card>

              {/* Article Statistics */}
              <Card className="kb-form-section" style={{ marginTop: '24px' }}>
                <div className="kb-section-title">
                  <TrendingUp size={18} />
                  Article Statistics
                </div>
                
                <div className="kb-statistics-grid">
                  <div className="kb-stat-card-small">
                    <div className="kb-stat-icon" style={{ backgroundColor: '#dcfce7' }}>
                      <ThumbsUp size={18} color="#10b981" />
                    </div>
                    <div>
                      <div className="kb-stat-number">{article.helpfulCount || 0}</div>
                      <div className="kb-stat-label">Helpful</div>
                    </div>
                  </div>
                  
                  <div className="kb-stat-card-small">
                    <div className="kb-stat-icon" style={{ backgroundColor: '#fee2e2' }}>
                      <ThumbsDown size={18} color="#ef4444" />
                    </div>
                    <div>
                      <div className="kb-stat-number">{article.notHelpfulCount || 0}</div>
                      <div className="kb-stat-label">Not Helpful</div>
                    </div>
                  </div>
                  
                  <div className="kb-stat-card-small">
                    <div className="kb-stat-icon" style={{ backgroundColor: '#f3e8ff' }}>
                      <TagIcon size={18} color="#8b5cf6" />
                    </div>
                    <div>
                      <div className="kb-stat-number">{article.tags?.length || 0}</div>
                      <div className="kb-stat-label">Tags</div>
                    </div>
                  </div>
                  
                  {/* Attachments Stat */}
                  <div className="kb-stat-card-small">
                    <div className="kb-stat-icon" style={{ backgroundColor: '#e0f2fe' }}>
                      <Paperclip size={18} color="#0ea5e9" />
                    </div>
                    <div>
                      <div className="kb-stat-number">{attachments.length}</div>
                      <div className="kb-stat-label">Attachments</div>
                    </div>
                  </div>
                </div>
              </Card>

              {/* Quick Actions */}
              <Card className="kb-form-section" style={{ marginTop: '24px' }}>
                <div className="kb-section-title">
                  <FileText size={18} />
                  Quick Actions
                </div>
                
                <div className="kb-related-articles">
                  <div 
                    className="kb-related-article" 
                    onClick={() => navigate('/knowledge-base')}
                    style={{ cursor: 'pointer' }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <div style={{ width: '32px', height: '32px', background: '#f3f4f6', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <BookOpen size={16} color="#4b5563" />
                      </div>
                      <div>
                        <Text strong style={{ fontSize: '15px' }}>Browse All Articles</Text>
                        <Text type="secondary" style={{ fontSize: '13px', display: 'block' }}>
                          View all knowledge base articles
                        </Text>
                      </div>
                    </div>
                  </div>
                  <Divider style={{ margin: '16px 0' }} />
                  
                  <div 
                    className="kb-related-article" 
                    onClick={() => navigate('/knowledge-base/new')}
                    style={{ cursor: 'pointer' }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <div style={{ width: '32px', height: '32px', background: '#f0f9ff', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Edit size={16} color="#0ea5e9" />
                      </div>
                      <div>
                        <Text strong style={{ fontSize: '15px' }}>Create New Article</Text>
                        <Text type="secondary" style={{ fontSize: '13px', display: 'block' }}>
                          Share your knowledge with others
                        </Text>
                      </div>
                    </div>
                  </div>
                  <Divider style={{ margin: '16px 0' }} />
                  
                  <div 
                    className="kb-related-article" 
                    onClick={() => navigate(`/knowledge-base/edit/${article.id || article.articleUuid}`)}
                    style={{ cursor: 'pointer' }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <div style={{ width: '32px', height: '32px', background: '#fef3c7', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <FileText size={16} color="#d97706" />
                      </div>
                      <div>
                        <Text strong style={{ fontSize: '15px' }}>Edit This Article</Text>
                        <Text type="secondary" style={{ fontSize: '13px', display: 'block' }}>
                          Update or improve this content
                        </Text>
                      </div>
                    </div>
                  </div>
                </div>
              </Card>
            </Col>
          </Row>
        </div>
      </div>

      {/* Support Status Modal */}
      <SupportStatusModal />
    </div>
  );
};

export default ArticleViewPage;